<template>
    <div class="page_data" :style="`width: ${getPageWebSize.px.width}px; margin: auto;`">
        <div class="page_data-index">
            <!-- Page {{ pageIndex + 1 }} of {{ getProject.pages.length }} -->
            {{ $t('editorScenePageNumber', { num1: `${ pageIndex + 1 }`, num2: `${ getProject.pages.length }` }) }}
        </div>
         <div class="page_data-nav">
            <button
                class="prev tooltipElement"
                @click="slidePage(-1)"
                :disabled="checkDisabledPrev"
            >
                <inline-svg
                    :src="arrow_up_icon"
                    :fill="svgFillPrev()"
                />
                <Tooltip :tooltipText="`${ $t('editorScenePageShowPrevious') }`" />
            </button>
            <button
                class="next tooltipElement"
                @click="slidePage(1)"
                :disabled="checkDisabledNext"
            >
                <inline-svg
                    :src="arrow_down_icon"
                    :fill="svgFillNext()"
                />
                <Tooltip :tooltipText="`${ $t('editorScenePageShowNext') }`" />
            </button>
        </div>
        <pageBackgroundImage :pageId="pageId" v-if="isVisibleWhenPageLocked" :bgImageOpen.sync="bgImageOpen" :pageSettingsActive="isActive" :bgImageActive="bgImageOpen" />
        <div
            class="pageSettings tooltipElement"
            :class="pageSettingsOpen ? 'active' : ''"
            v-if="isVisibleWhenPageLocked"
        >
            <div
                class="pageSettings_trigger"
                :class="pageSettingsOpen ? 'tooltipHide' : ''"
                @click="isActive = !isActive; bgImageOpen = false"
            >
                <inline-svg
                    :src="pageSettings_icon"
                    :fill="isActive ? `#${this.getSessionDetails.editorSetup.colors.primaryColor}` : '#707070'"
                />
            </div>
            <div class="pageSettings_container">
                <div v-if="isVisibleWhenPageLocked">
                    <strong>{{ $t('editorScenePageBackgroundColor') }}</strong>
                    <div style="width: 100px;" >
                        <colorPicker :pageId="pageId"/>
                    </div>
                </div>
                <div v-if="getSessionDetails.mode === 'merchant'">
                    <strong>{{ $t('editorScenePageCornerRadius') }}</strong>
                    <div class="page_radius">
                        <input
                            id="pageRadius"
                            v-model.number="pageRadius"
                            type="number"
                            name="pageRadius"
                            min="0"
                            max="100"
                            @input="onPageRadiusInputChange"
                        />
                        %
                    </div>
                </div>
                <div v-if="getSessionDetails.mode === 'merchant'">
                    <strong>{{ $t('editorScenePageBackgroundAdditional') }}</strong>
                    <div>
                        <button
                            v-if="getProject.pages.length > 1"
                            class="page_data-remove"
                            @click="removePage"
                        >
                            {{ $t('editorScenePageDelete') }}
                        </button>
                        <button
                            class="page_data-lock"
                            :class="isLocked ? 'isLocked' : ''"
                            @click="lockPage"
                        >
                            {{ $t('editorScenePageLock') }}
                        </button>
                    </div>
                </div>
            </div>
            <Tooltip :tooltipText="`${ $t('editorScenePageSettings') }`" />
        </div>
        <div class="page_data-number" v-if="getSessionDetails.mode === 'merchant'">
            <button
                @click="addNewPage"
                class="page_data-add tooltipElement"
            >
                <inline-svg
                    :src="add_icon"
                    fill="#737373"
                />
                <Tooltip :tooltipText="`${ $t('editorScenePageAddNew') }`" />
            </button>
        </div>
        <Modal 
            v-if="showDeleteModal"
            @close="closeWarningModal"
        >
            <template v-slot:header>
                <strong>{{ $t('editorScenePageWarning') }}</strong>
            </template>
            <template v-slot:body>
                <div class="modalForm">
                    <div class="form-footer">
                        <button @click="showDeleteModal = false" class="button modal_cancel-btn">{{ $t('editorCancel') }}</button>
                        <button
                            @click="removePageTest(pageToRemove)"
                            class="button main_btn form_submit_btn"
                        >{{ $t('editorScenePageDeleteConfirm') }}</button>
                    </div>
                </div>
            </template>
        </Modal>
    </div>
</template>

<script>
    import { mapState, mapGetters } from 'vuex';
    import { uuid } from 'vue-uuid';
    import colorPicker from './pageColorPicker.vue';
    import pageBackgroundImage from './PageBackgroundImage.vue';
    import arrow_down_icon from '@/assets/icons/arrow_down.svg';
    import arrow_up_icon from '@/assets/icons/arrow_up.svg';
    import add_icon from '@/assets/icons/add.svg';
    import pageSettings_icon from '@/assets/icons/pageSettings.svg';
    import Modal from '@/components/Global/Modal.vue';
    import Tooltip from '@/components/Global/Tooltip.vue';

    export default {
        props: ['pageIndex', 'pageId'],
        data() {
            return {
                arrow_down_icon,
                arrow_up_icon,
                add_icon,
                pageSettings_icon,
                showDeleteModal: false,
                pageToRemove: null,
                pageRadius: 0,
                isLocked: false,
                isActive: false,
                bgImageOpen: false
            }
        },
        methods: {
            slidePage(change) {
                this.$store.dispatch('CHANGE_PAGE_TRANSITION', this.getPageTransition + change);
                this.$store.dispatch('SET_CURRENT_PAGE', this.getPages[this.getPageTransition].content.id);
            },
            svgFillPrev() {
                return this.checkDisabledPrev ? '#707070' : '#737373'
            },
            svgFillNext() {
                return this.checkDisabledNext ? '#707070' : '#737373'
            },
            onPageRadiusInputChange() {
                this.$store.dispatch('SET_PAGE_RADIUS', this.pageRadius);
            },
            removePage() {
                const hasElements = this.getPages.find(page => page.content.id === this.pageId).content.elements.length > 0 ? true : false;
                hasElements ? (this.showDeleteModal = true, this.pageToRemove = this.pageId) : this.removePageTest(this.pageId);
            },
            removePageTest(pageId) {
                this.$store.dispatch('REMOVE_PAGE', pageId);
                this.$store.dispatch('CHANGE_ACTIVE_ELEMENT', {});
                this.$store.dispatch('CHANGE_PAGE_TRANSITION', { deleteAction: true });
                this.$store.dispatch('SET_CURRENT_PAGE', this.getPages[0].content.id);
            },
            lockPage() {
                this.isLocked = !this.isLocked;
                this.$store.dispatch('LOCK_PAGE', this.isLocked);
            },
            closeWarningModal() {
                this.showDeleteModal = false;
                this.pageToRemove = null;
            },
            addNewPage() {
                this.$store.dispatch('DISABLE_ELEMENTS_ON_PAGE');
                this.$store.dispatch('CHANGE_ACTIVE_ELEMENT', {})

                const pageCount = this.getPages.length;
                const activePageIndex = this.getActivePageIndex;

                activePageIndex < pageCount -1 ? (
                    this.$store.dispatch('CHANGE_PAGE_TRANSITION', this.getPageTransition + (pageCount - activePageIndex))
                ) : (
                    this.$store.dispatch('CHANGE_PAGE_TRANSITION', this.getPageTransition + 1)
                );
                
                const newPage = {
                    html: null,
                    content: {
                        id: uuid.v1(),
                        isActive: true,
                        elements: [],
                        zIndex: 0,
                        pageColor: {
                            rgb: 'rgb(255, 255, 255)',
                            hex: 'FFFFFF',
                            X: 0,
                            Y: 150
                        },
                        pageRadius: 0,
                        isLocked: false,
                        backgroundImage: {
                            url: null,
                            backgroundPosition: 'center',
                            backgroundSize: 'cover',
                            backgroundRepeat: 'no-repeat'
                        }
                    }
                }
                this.$store.dispatch('ADD_NEW_PAGE', newPage);
                
                this.$store.dispatch('SET_CURRENT_PAGE', newPage.content.id);
            }
        },
        computed: {
            ...mapState([
                'currentPageIndex'
            ]),
            ...mapGetters([
                'getProject',
                'getActivePageId',
                'getPages',
                'getActivePageIndex',
                'getActivePage',
                'getPageWebSize',
                'getSessionDetails',
                'getPageTransition'
            ]),
            checkDisabledPrev() {
                return this.getPageTransition === 0 ? true : false;
            },
            checkDisabledNext() {
                return this.getPageTransition === (this.getProject.pages.length - 1) ? true : false;
            },
            isVisibleWhenPageLocked() {
                if (this.getSessionDetails.mode === 'user') {
                    if (this.isLocked) {
                        return false;
                    } return true;
                } return true;
            },
            pageSettingsOpen() {
                if (this.isActive === true && this.bgImageOpen !== true) {
                    return true;
                } return false;
            }
        },
        watch: {
            'bgImageOpen': {
                deep: true,
                handler() {
                    if (this.bgImageOpen) {
                        this.isActive = false;
                    }
                }
            },
            'getActivePage': {
                deep: true,
                handler() {
                    this.isLocked = this.getActivePage.content.isLocked;
                }
            }
        },
        created() {
            this.pageRadius = this.getPages.find(page => page.content.id === this.pageId).content.pageRadius;
            this.isLocked = this.getPages.find(page => page.content.id === this.pageId).content.isLocked;
        },
        components: {
            colorPicker,
            pageBackgroundImage,
            Modal,
            Tooltip
        }
    }
</script>

<style lang="scss">
    @import '@/styles/setup/_variables.scss';

    .page_data {
        display: flex;
        align-items: center;
        box-sizing: border-box;
        padding: 8px 0;
        z-index: 100;
    }
    .page_data-nav {
        margin: 0 8px;
        display: flex;
    }
    .page_data-nav button,
    .page_data-add {
        background-color: $white;
        border: 1px solid $white;
        border-radius: 4px;
        margin: 0 3px;
        cursor: pointer;
        display: flex;
        align-content: center;
        justify-content: center;
    }
    .page_data-remove,
    .page_data-lock {
        background-color: transparent;
        border: none;
        cursor: pointer;
    }
    .page_data-nav button:disabled {
        background-color: #F9F9F9;
        border-color: #F9F9F9;
        cursor: not-allowed;
    }
    .page_data-number {
        margin: 0 0 0 auto;
        display: flex;
        justify-content: flex-end;
    }
    .page_radius {
        width: 50px;
        display: flex;
        align-items: center;
        margin-right: 8px;
    }
    .page_radius input {
        width: 60%;
        border-radius: 3px;
        border: 1px solid white;
        padding: 4px;
    }
    .pageSettings {
        position: relative;

        .pageSettings_trigger {
            cursor: pointer;
            display: flex;
            align-items: center;
            justify-content: center;
            padding: 4px;
            margin: 0 5px;
        }

        .pageSettings_container {
            display: none;
            padding: 15px 15px 5px 15px;
            background-color: #f5f5f5;
            box-shadow: 2px 3px 2px 0px #b7b7b7;
            position: absolute;
            width: 355px;
            top: 22px;
            left: 5px;
            color: #707070;
            font-size: 12px;
            z-index: 100;

            > div {
                margin-bottom: 20px;

                > strong {
                    margin-bottom: 10px;
                    display: block;
                }

                > div {
                    border-left: 1px solid #707070;
                    padding-left: 5px;

                    > div,
                    > button {
                        text-align: center;
                        font-size: 12px;
                        border: unset;
                        color: #707070;
                        background-color: transparent;
                        border-right: 1px solid #707070;
                        padding: 5px 10px 5px 0;
                        margin-right: 10px;
                    }

                    > button:hover {
                        color: var(--primary-color);
                    }

                    > .isLocked {
                        color: var(--primary-color);
                    }
                }
            }
            .color_picker {
                padding: 0 6px 0 0;
            }
        }

        &.active {
            .pageSettings_trigger {
                background-color: #f5f5f5;
                box-shadow: 2px 0px 4px 0px #b7b7b7;
            }
            .pageSettings_container {
                display: block;
            }
        }
        &:hover {
            svg {
                fill: var(--primary-color);
            }
        }
    }
</style>